/*
 * Cart upsell row — keeps its Swiper alive across cart re-renders, on both surfaces that render it
 * (the cart drawer and the cart page's item list).
 *
 * cart-drawer.js swaps #CartDrawer's innerHTML from the Section Rendering API on every cart
 * change, and cart.js replaces the whole <cart-items> innerHTML on the cart page whenever the cart
 * changes from another source. Either swap throws away the slider nodes and the Swiper instance
 * bound to them. theme-vendors.js only initialises sliders on DOMContentLoaded and
 * shopify:section:load, and neither fires for those swaps, so without this the row comes back as a
 * dead stack of slides the first time the cart changes — or never initialises at all for a drawer
 * whose cart started empty, since the row is not in the drawer's empty state.
 */
(function () {
  // Each surface's outermost element that survives its own re-render, so one observer per surface
  // covers every swap underneath it.
  const ROOT_SELECTORS = ['cart-drawer', 'cart-items'];
  const SLIDER_SELECTOR = '.cd-upsell .swiper[data-slider-options]';

  function initSliders(root) {
    if (!root.querySelector(SLIDER_SELECTOR)) return;

    // theme-vendors.js owns slider setup for the whole theme: same option parsing, same
    // already-initialised guard. Only fall back to a bare Swiper if it is not on the page.
    if (typeof window.initSlideshow === 'function') {
      window.initSlideshow(root);
      return;
    }

    if (typeof window.Swiper !== 'function') return;

    root.querySelectorAll(SLIDER_SELECTOR).forEach(function (slider) {
      if (slider.swiper) return;

      let options = {};

      try {
        options = JSON.parse(slider.dataset.sliderOptions || '{}');
      } catch (error) {
        console.warn('Cart drawer products slider options are invalid:', error, slider);
        return;
      }

      new window.Swiper(slider, options);
    });
  }

  // The drawer is off-canvas when the page loads, so Swiper measures it before it has been laid
  // out at its open width. Re-measuring on open is what stops the first slide from sitting at the
  // wrong offset.
  function updateSliders(root) {
    root.querySelectorAll(SLIDER_SELECTOR).forEach(function (slider) {
      if (slider.swiper) slider.swiper.update();
    });
  }

  // Products with options choose their variant in the card, so the price next to the title has to
  // follow the select. Values come from the option's data attributes rather than a second fetch:
  // the drawer already rendered every variant's price when it rendered the select.
  function syncVariantPrice(select) {
    const card = select.closest('.cd-upsell__card');
    const option = select.options[select.selectedIndex];

    if (!card || !option) return;

    const current = card.querySelector('.cd-upsell__price-current');
    const wasPrice = card.querySelector('.cd-upsell__price-was');

    if (current && option.dataset.price) current.textContent = option.dataset.price;

    if (!wasPrice) return;

    if (option.dataset.compare) {
      wasPrice.textContent = option.dataset.compare;
      wasPrice.hidden = false;
    } else {
      wasPrice.hidden = true;
    }
  }

  // Delegated from the document: every surface's markup is replaced wholesale on a cart update, so
  // a listener bound to the select itself would die with the old markup, and one listener here
  // covers the drawer and the cart page without caring which of them is on the page.
  function watchVariantSelects() {
    document.addEventListener('change', function (event) {
      const select = event.target.closest && event.target.closest('[data-cd-variant-select]');
      if (select) syncVariantPrice(select);
    });
  }

  function watchRoot(root) {
    let pending = null;
    // Sticky across every batch the debounce swallows, not a per-callback local: cart.js replaces
    // the drawer's innerHTML and then toggles the loading spinner's class, so the class-only batch
    // arrives inside the 50ms window and its clearTimeout used to cancel the childList batch's
    // init — the fresh slider nodes were only ever `update()`d, never initialised.
    let needsInit = false;

    const observer = new MutationObserver(function (mutations) {
      needsInit =
        needsInit ||
        mutations.some(function (mutation) {
          return mutation.type === 'childList' && mutation.addedNodes.length > 0;
        });

      clearTimeout(pending);
      pending = setTimeout(function () {
        // Swiper mutates the row as it initialises (bullets, slide classes, inline transforms).
        // Watching through that would re-trigger this callback forever, so the observer is off
        // while the work runs and back on once the DOM has settled.
        observer.disconnect();

        if (needsInit) initSliders(root);
        needsInit = false;
        updateSliders(root);

        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }, 50);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  function start() {
    watchVariantSelects();

    ROOT_SELECTORS.forEach(function (selector) {
      const root = document.querySelector(selector);
      if (!root) return;

      initSliders(root);
      watchRoot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
